import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, muted, table } from "../../ui/record";
import { field, pills, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";

// Settings › Services and prices (brief 1E-16, mockup v67 and v68 settings/services; brief 1E-24,
// mockup v84): Units (what the company bills by and how each is counted, each in a module; the
// Module filter, Add unit, the pencil, Archive and Restore with Show archived), Services (what each
// business offers, in which module, the units of its module it's billed in), Standard prices (each business's
// prices with no contract, by version; a change starts a new version on a date) and Time periods
// (Weekend, Nights, Evenings, Holidays). No claim codes anywhere: those live on a client's CMS-1500
// setup. Reading needs Settings at View; changing Admin or Settings at Edit (prices also Billing
// at Edit).

interface UnitView {
  id: string;
  name: string;
  label: string;
  countedBy: string;
  countedByLabel: string;
  rules: Record<string, unknown>;
  how: string;
  modules: string[];
  module: string;
  archived: string | null;
}
interface Choice {
  value: string;
  label: string;
}
interface ServiceView {
  id: string;
  name: string;
  module: string;
  moduleLabel: string;
  businessId: string | null;
  givenBy: string;
  unitIds: string[];
  units: string;
}
interface LineView {
  id: string;
  serviceId: string | null;
  service: string;
  unitId: string;
  unit: string;
  rate: number;
  rateWords: string;
  rule: string | null;
}
interface VersionView {
  id: string;
  number: number;
  fromWords: string;
  toWords: string | null;
  note: string;
  lines: LineView[];
}
interface Answer {
  today: string;
  canEdit: boolean;
  canEditPrices: boolean;
  modules: Choice[];
  kinds: { rounding: Choice[]; from: Choice[]; miles: Choice[]; waive: Choice[] };
  businesses: { id: string; name: string }[];
  units: UnitView[];
  services: ServiceView[];
  standard: { businessId: string; business: string; current: VersionView | null }[];
  periods: {
    id: string;
    name: string;
    words: string;
    covers: { kind: string; days?: number[]; from?: string; to?: string };
  }[];
}

type Tab = "units" | "services" | "standard" | "periods";

const KINDS: [string, string][] = [
  ["time", "Time"],
  ["distance", "Distance"],
  ["count", "A count"],
  ["flat", "Flat fee"],
  ["cancellation", "Cancellation"],
];
const pairs = (p: readonly Choice[]) => p.map((x) => [x.value, x.label] as const);
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export async function servicesScreen(host: HTMLElement, ctx: ScreenContext) {
  let tab: Tab = "units";
  // Brief 1E-24: the lists filter by module; archived units show when asked.
  let moduleShown = "";
  let showArchived = false;
  const add = el("button", { type: "button", class: "btn pri" });
  const head = pageHead(ctx, "Units, the services each business offers, and standard prices.", [
    add,
  ]);
  const tabs = el("div", { class: "tabs", role: "tablist" });
  const slot = el("section", { class: "card" });
  host.append(head, tabs, slot);
  let a: Answer | null = null;

  async function load() {
    const res = await api<Answer>("GET", "/api/settings/services");
    if (!res.ok) {
      slot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Services and prices couldn't be loaded."),
      );
      return;
    }
    a = res.body;
    draw();
  }

  function draw() {
    if (!a) return;
    const x = a;
    tabs.replaceChildren(
      ...(
        [
          ["units", "Units"],
          ["services", "Services"],
          ["standard", "Standard prices"],
          ["periods", "Time periods"],
        ] as [Tab, string][]
      ).map(([t, l]) => {
        const b = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(tab === t) },
          l,
        );
        b.addEventListener("click", () => {
          tab = t;
          draw();
        });
        return b;
      }),
    );
    const label = tab === "units" ? "Add unit" : tab === "services" ? "Add service" : "New version";
    add.replaceChildren(icon("plus"), ` ${label}`);
    const can = tab === "standard" ? x.canEditPrices : x.canEdit;
    add.style.display = can && tab !== "periods" ? "" : "none";
    if (tab === "units") slot.replaceChildren(...unitsTab(x));
    else if (tab === "services") slot.replaceChildren(...servicesTab(x));
    else if (tab === "standard") slot.replaceChildren(...standardTab(x));
    else slot.replaceChildren(...periodsTab(x));
  }

  const hint = (t: string) => el("p", { class: "small muted", style: "margin:0 0 8px" }, t);

  /** The Module filter over the units and services lists. */
  function moduleFilter(x: Answer): HTMLElement {
    const pick = el(
      "select",
      { "aria-label": "Module" },
      el("option", { value: "", selected: moduleShown === "" }, "All"),
      ...x.modules.map((m) =>
        el("option", { value: m.value, selected: moduleShown === m.value }, m.label),
      ),
    );
    pick.addEventListener("change", () => {
      moduleShown = pick.value;
      draw();
    });
    return el(
      "label",
      { class: "small", style: "display:inline-flex;gap:6px;align-items:center" },
      "Module ",
      pick,
    );
  }

  function unitsTab(x: Answer): Node[] {
    const inModule = x.units.filter((u) => !moduleShown || u.modules.includes(moduleShown));
    const archived = inModule.filter((u) => u.archived).length;
    const shown = inModule.filter((u) => showArchived || !u.archived);
    const toggle = el(
      "button",
      { type: "button", class: "btn sm ghost" },
      `${showArchived ? "Hide" : "Show"} archived (${archived})`,
    );
    toggle.addEventListener("click", () => {
      showArchived = !showArchived;
      draw();
    });
    const act = async (u: UnitView, what: "archive" | "restore") => {
      const res = await api("POST", `/api/settings/units/${u.id}/${what}`, {});
      if (!res.ok)
        return toast(
          `${u.name} couldn't be ${what === "archive" ? "archived" : "restored"}`,
          res.body.error,
        );
      toast(what === "archive" ? "Archived" : "Restored", u.name);
      await load();
    };
    return [
      el(
        "div",
        { class: "cardhead", style: "margin:0 0 8px" },
        moduleFilter(x),
        archived ? toggle : null,
      ),
      table(
        [
          { h: "Unit", f: (u: UnitView) => el("b", {}, u.name) },
          { h: "Kind", f: (u) => u.countedByLabel },
          { h: "How", f: (u) => el("span", { class: "small" }, u.how) },
          { h: "Module", f: (u) => el("span", {}, ...u.module.split(", ").map((m) => chip(m))) },
          {
            h: "",
            num: true,
            f: (u) => {
              if (!x.canEdit)
                return u.archived
                  ? el("span", { class: "small muted" }, `Archived ${u.archived}`)
                  : "";
              if (u.archived) {
                const b = el("button", { type: "button", class: "btn sm ghost" }, "Restore");
                b.addEventListener("click", () => void act(u, "restore"));
                return el(
                  "span",
                  {},
                  el("span", { class: "small muted" }, `Archived ${u.archived} `),
                  b,
                );
              }
              return el(
                "span",
                { class: "rowbtns" },
                ib("pencil", `Change ${u.name}`, () => unitModal(x, u)),
                ib("archive", `Archive ${u.name}`, () => void act(u, "archive")),
              );
            },
          },
        ],
        shown,
        undefined,
        "Units",
      ),
    ];
  }

  function servicesTab(x: Answer): Node[] {
    const shown = x.services.filter((s) => !moduleShown || s.module === moduleShown);
    return [
      el("div", { class: "cardhead", style: "margin:0 0 8px" }, moduleFilter(x)),
      shown.length
        ? table(
            [
              { h: "Service", f: (s: ServiceView) => el("b", {}, s.name) },
              { h: "Module", f: (s) => chip(s.moduleLabel) },
              { h: "Given by", f: (s) => s.givenBy },
              { h: "Billed in", f: (s) => el("span", { class: "small" }, s.units || "–") },
              {
                h: "",
                num: true,
                f: (s) =>
                  x.canEdit ? ib("pencil", `Change ${s.name}`, () => serviceModal(x, s)) : "",
              },
            ],
            shown,
            undefined,
            "Services",
          )
        : muted("No services yet."),
      el(
        "p",
        { class: "small muted", style: "margin:8px 0 0" },
        "Billed in lists only units from the service's module.",
      ),
    ];
  }

  function standardTab(x: Answer): Node[] {
    const out: Node[] = [
      hint(
        "What each business charges when there's no contract. A contract's bill rate card starts from these.",
      ),
    ];
    for (const s of x.standard) {
      const v = s.current;
      out.push(
        el(
          "h3",
          { class: "h3s", style: "margin:12px 0 6px" },
          s.business,
          v
            ? el("span", { class: "small muted" }, ` · version ${v.number} · from ${v.fromWords}`)
            : null,
        ),
        v && v.lines.length
          ? table(
              [
                { h: "Service", f: (l: LineView) => el("b", {}, l.service) },
                { h: "Unit", f: (l) => l.unit },
                { h: "Price", num: true, f: (l) => el("span", {}, `$${l.rate.toFixed(2)}`) },
                {
                  h: "",
                  num: true,
                  f: (l) =>
                    x.canEditPrices
                      ? ib("pencil", `Change ${l.service}`, () => priceModal(x, s.businessId, l))
                      : "",
                },
              ],
              v.lines,
              undefined,
              `Standard prices of ${s.business}`,
            )
          : muted("No standard prices yet."),
      );
    }
    if (!x.standard.length)
      out.push(muted("Pick a business for each module in Settings › Modules."));
    return out;
  }

  function periodsTab(x: Answer): Node[] {
    return [
      hint("Named once and used by bill rate card differentials and by pay."),
      table(
        [
          { h: "Period", f: (p: Answer["periods"][number]) => el("b", {}, p.name) },
          { h: "Covers", f: (p) => p.words },
          {
            h: "",
            num: true,
            f: (p) => (x.canEdit ? ib("pencil", `Change ${p.name}`, () => periodModal(p)) : ""),
          },
        ],
        x.periods,
        undefined,
        "Time periods",
      ),
    ];
  }

  // ---- Sheets -------------------------------------------------------------------------------------------

  /** Add a unit, or its pencil (brief 1E-24, mockup v84): name, module, kind and its rules. */
  function unitModal(x: Answer, u?: UnitView) {
    const name = field("Name", u?.name ?? "", { placeholder: "e.g. Session, Credit, Month" });
    const module = select(
      "Module",
      pairs(x.modules),
      u?.modules.find((m) => x.modules.some((y) => y.value === m)) ??
        (moduleShown || x.modules[0]?.value),
    );
    const kind = select("Kind", KINDS, u?.countedBy ?? "time");
    const rules = el("div", { class: "form formgrid", style: "max-width:none" });
    let read: () => Record<string, unknown> = () => ({});
    const r = u?.rules ?? {};
    const num = (v: unknown) => (v === null || v === undefined || v === 0 ? "" : String(v));
    const drawRules = () => {
      const k = kind.input.value;
      // Rules from the unit as it is, only while its own kind is picked.
      const was = (k === u?.countedBy ? r : {}) as Record<string, unknown>;
      if (k === "time") {
        const per = field("Billed per (minutes)", String(was.minutes ?? 15), {
          inputmode: "numeric",
        });
        const round = select(
          "Rounding",
          pairs(x.kinds.rounding),
          x.kinds.rounding.some((o) => o.value === was.rounding) ? String(was.rounding) : "exact",
        );
        const free = field("Free minutes", num(was.free), {
          inputmode: "numeric",
          placeholder: "None",
        });
        const cap = field("Cap (minutes)", num(was.cap), {
          inputmode: "numeric",
          placeholder: "No cap",
        });
        const from = select("Counted from", pairs(x.kinds.from), String(was.from ?? "clock"));
        rules.replaceChildren(per.el, round.el, free.el, cap.el, from.el);
        read = () => ({
          minutes: per.input.value,
          rounding: round.input.value,
          free: free.input.value,
          cap: cap.input.value,
          from: from.input.value,
        });
      } else if (k === "cancellation") {
        const body = el("tbody");
        const rows: {
          under: HTMLInputElement;
          charge: HTMLInputElement;
          how: HTMLSelectElement;
        }[] = [];
        const addRow = (t?: { under: number; percent: number | null; amount: number | null }) => {
          const under = el("input", {
            type: "text",
            inputmode: "numeric",
            value: t ? String(t.under) : "",
            "aria-label": "Notice under (hours)",
            style: "width:80px",
          });
          const charge = el("input", {
            type: "text",
            inputmode: "decimal",
            value: t ? String(t.percent ?? t.amount ?? "") : "",
            "aria-label": "Charge",
            style: "width:90px",
          });
          const how = el(
            "select",
            { "aria-label": "Charged as" },
            el("option", { value: "percent", selected: !t || t.percent !== null }, "% of the trip"),
            el("option", { value: "amount", selected: !!t && t.percent === null }, "Fixed ($)"),
          );
          rows.push({ under, charge, how });
          body.append(
            el("tr", {}, el("td", {}, "Under ", under, " hours"), el("td", {}, charge, " ", how)),
          );
        };
        const tiers = Array.isArray(was.tiers)
          ? (was.tiers as { under: number; percent: number | null; amount: number | null }[])
          : [];
        if (tiers.length) tiers.forEach((t) => addRow(t));
        else addRow({ under: 24, percent: 100, amount: null });
        const more = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add a row");
        more.addEventListener("click", () => addRow());
        const waive = select(
          "Can be waived by",
          pairs(x.kinds.waive),
          String(was.waive ?? "billing"),
        );
        rules.replaceChildren(
          el(
            "div",
            { class: "field wide" },
            el("label", {}, "By notice"),
            el(
              "div",
              { class: "tw" },
              el(
                "table",
                {},
                el("thead", {}, el("tr", {}, el("th", {}, "Notice"), el("th", {}, "Charge"))),
                body,
              ),
            ),
            more,
          ),
          waive.el,
        );
        read = () => ({
          tiers: rows
            .filter((x) => x.under.value.trim() || x.charge.value.trim())
            .map((x) => ({
              under: x.under.value,
              percent: x.how.value === "percent" ? x.charge.value : null,
              amount: x.how.value === "amount" ? x.charge.value : null,
            })),
          waive: waive.input.value,
        });
      } else if (k === "distance") {
        const from = select("Miles from", pairs(x.kinds.miles), String(was.from ?? "trip"));
        const included = field("Miles included", num(was.includedMiles), {
          inputmode: "numeric",
          placeholder: "None",
        });
        const dead = field(
          "Miles to the pickup counted past",
          was.deadPast === null || was.deadPast === undefined ? "" : String(was.deadPast),
          { inputmode: "numeric", placeholder: "Not counted" },
        );
        rules.replaceChildren(from.el, included.el, dead.el);
        read = () => ({
          from: from.input.value,
          includedMiles: included.input.value || 0,
          deadPast: dead.input.value,
        });
      } else {
        const per = field("One per", String(was.per ?? ""), {
          placeholder: "e.g. Visit, Trip leg, Session, Month",
        });
        if (k === "flat") {
          rules.replaceChildren(per.el);
          read = () => ({ per: per.input.value });
        } else {
          // Brief 1E-19: a count included each week or month (one free ride a month).
          const included = field("Included each period", String(was.includedCount ?? 0), {
            inputmode: "numeric",
          });
          const each = select(
            "Each",
            [
              ["month", "Month"],
              ["week", "Week"],
            ],
            String(was.includedPer ?? "month"),
          );
          rules.replaceChildren(per.el, included.el, each.el);
          read = () => ({
            per: per.input.value,
            includedCount: Number(included.input.value || 0),
            includedPer: each.input.value,
          });
        }
      }
    };
    kind.input.addEventListener("change", drawRules);
    drawRules();
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: u ? `Change ${u.name}` : "Add a unit",
      body: [
        el(
          "div",
          { class: "form" },
          el("div", { class: "formgrid" }, name.el, module.el),
          kind.el,
          rules,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api<{ unit: UnitView }>(
        u ? "PUT" : "POST",
        u ? `/api/settings/units/${u.id}` : "/api/settings/units",
        {
          name: name.input.value,
          module: module.input.value,
          countedBy: kind.input.value,
          rules: read(),
        },
      );
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Saved", res.body.unit.how);
      await load();
    });
  }

  function serviceModal(x: Answer, s?: ServiceView) {
    const name = field("Name", s?.name ?? "", { placeholder: "e.g. Wheelchair van" });
    const module = select(
      "Module",
      x.modules.map((m) => [m.value, m.label] as const),
      s?.module ?? x.modules[0]?.value,
    );
    module.input.disabled = !!s;
    const by = select(
      "Given by",
      [["", "The module's business"], ...x.businesses.map((b) => [b.id, b.name] as const)],
      s?.businessId ?? "",
    );
    // Brief 1E-24: Billed in offers only the module's units (an archived one isn't offered).
    const unitsSlot = el("div", { class: "wide" });
    let units = pills("Billed in", "checkbox", [], []);
    const drawUnits = () => {
      const chosen = units.values().length ? units.values() : (s?.unitIds ?? []);
      units = pills(
        "Billed in",
        "checkbox",
        x.units
          .filter((u) => !u.archived && u.modules.includes(module.input.value))
          .map((u) => [u.id, u.name] as const),
        chosen,
      );
      unitsSlot.replaceChildren(units.el);
    };
    module.input.addEventListener("change", drawUnits);
    drawUnits();
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: s ? `Change ${s.name}` : "Add a service",
      body: [el("div", { class: "form" }, name.el, module.el, by.el, unitsSlot)],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api(
        s ? "PUT" : "POST",
        s ? `/api/settings/services/${s.id}` : "/api/settings/services",
        {
          name: name.input.value,
          module: module.input.value,
          businessId: by.input.value || null,
          unitIds: units.values(),
        },
      );
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Saved", name.input.value);
      await load();
    });
  }

  /** A standard price, new or changed: always a new version from a date. */
  function priceModal(x: Answer, businessId?: string, line?: LineView) {
    const biz = select(
      "Business",
      x.standard.map((s) => [s.businessId, s.business] as const),
      businessId ?? x.standard[0]?.businessId,
    );
    biz.input.disabled = !!line;
    const service = select(
      "Service",
      [["", "Pick a service"], ...x.services.map((s) => [s.id, s.name] as const)],
      line?.serviceId ?? "",
    );
    const live = x.units.filter((u) => !u.archived || u.id === line?.unitId);
    const unit = select(
      "Unit",
      live.map((u) => [u.id, u.label] as const),
      line?.unitId ?? live[0]?.id,
    );
    service.input.disabled = !!line;
    unit.input.disabled = !!line;
    const rate = field("Price ($)", line ? line.rate.toFixed(2) : "", {
      inputmode: "decimal",
      placeholder: "35.00",
    });
    const from = field("Starts", x.today, { type: "date" });
    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      " Save as a new version",
    );
    const m = openModal({
      title: line ? `Change ${line.service}` : "New version of standard prices",
      sub: "The other prices carry over to the new version. Work before the start keeps the current prices.",
      body: [
        el(
          "div",
          { class: "form formgrid", style: "max-width:none" },
          biz.el,
          service.el,
          unit.el,
          rate.el,
          from.el,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api<{ version: { number: number } }>(
        "POST",
        `/api/settings/standard/${biz.input.value}/lines`,
        {
          lineId: line?.id,
          serviceId: service.input.value || null,
          unitId: unit.input.value,
          rate: rate.input.value,
          from: from.input.value,
        },
      );
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast(`Version ${res.body.version.number} starts ${from.input.value}`);
      await load();
    });
  }

  function periodModal(p: Answer["periods"][number]) {
    const name = field("Name", p.name);
    const holidays = p.covers.kind === "holidays";
    const days = pills(
      "Days",
      "checkbox",
      DAYS.map((d, i) => [String(i), d] as const),
      (p.covers.days ?? []).map(String),
    );
    const fromT = field("From", p.covers.from ?? "00:00", { type: "time" });
    const toT = field("To", p.covers.to ?? "00:00", { type: "time" });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: `Change ${p.name}`,
      sub: holidays ? "Covers the company's holiday list (Week, overtime & PTO)." : "",
      body: [
        el(
          "div",
          { class: "form" },
          name.el,
          ...(holidays ? [] : [days.el, el("div", { class: "formgrid" }, fromT.el, toT.el)]),
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api("PUT", `/api/settings/periods/${p.id}`, {
        name: name.input.value,
        covers: holidays
          ? { kind: "holidays" }
          : {
              kind: "times",
              days: days.values().map(Number),
              from: fromT.input.value,
              to: toT.input.value,
            },
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Saved", name.input.value);
      await load();
    });
  }

  add.addEventListener("click", () => {
    if (!a) return;
    if (tab === "units") unitModal(a);
    else if (tab === "services") serviceModal(a);
    else if (tab === "standard") priceModal(a);
  });
  await load();
}
